import { assign } from 'lodash';
import mixpanel from 'mixpanel-browser';

import { E2E_TEST_USER_SHARED_NAME } from '../../utils/e2e-testing-users';

export enum UserTrackingEvents {
  LOGIN = 'login',
  SESSION = 'session',
  PAGE_VISIT = 'page visit',
  PAGE_LEAVE = 'page_leave',
  OPEN_MENU = 'open menu',
  TAB_CHANGE = 'tab change',
  TOOLTIP_OPEN = 'tooltip open',
}

export enum InitiationTrackingEvents {
  FINISH_TOUR = 'finish tour',
  SKIP_TOUR = 'skip tour',
}

export enum CTAInteractionTrackingEvents {
  START_TRIAL = 'start trial',
  SIGN_UP_SUCCESS = 'sign up success',
  PROVIDE_ONBOARDING_INFO = 'provide onboarding info',
  CONTACT_SALES_CLICK = 'contact sales click',
  RESEND_VERIFICATION_EMAIL = 'resend verification email click',
  TRIAL_UPGRADE = 'new sales reach out trial',
}

export enum FeatureTrackingEvents {
  DATA_DOWNLOAD = 'data download',
  PNG_DOWNLOAD = 'png download',
  PPT_DOWNLOAD = 'ppt download',
  PPT_PREVIEW = 'ppt preview',
  PLOT_LINK_GENERATE = 'plot link generate',
}

// Saved Sets focused events – keep separate to avoid bloating "feature" bucket
export enum SavedSetsEvents {
  OPEN = 'saved sets open',
  CREATE = 'saved sets create',
  DELETE = 'saved sets delete',
  RENAME = 'saved sets rename',
  APPLY = 'saved sets apply',
  OVERWRITE = 'saved sets overwrite',
}

export enum SourceWhaleTrackingEvents {
  ONBOARDING_OPEN = 'sourcewhale onboarding open',
  EXPORT_OPEN = 'sourcewhale export open',
  EXPORT_CAMPAIGN = 'sourcewhale export campaign',
  EXPORT_CONTACTS = 'sourcewhale export contacts',
}

export enum TutorialTrackingEvents {
  START_TUTORIAL_VIDEO = 'start tutorial video',
  WATCH_TUTORIAL_VIDEO = 'watched tutorial video',
  END_TUTORIAL_VIDEO = 'end tutorial video',
}

export enum OnboardingTrackingEvents {
  TEST_A_NEWS_BUTTON_CLICK = 'test (a) news button click',
}

export enum ABTestingTrackingEvents {
  TEST_A_SESSION = 'test (a) session',
  TEST_A_SIGNUP = 'test (a) sign up',
  TEST_B_SESSION = 'test (b) session',
  TEST_B_SIGNUP = 'test (b) sign up',
}

export enum BuildADashTrackingEvents {
  CREATE_NEW_PAGE = 'create new page',
  CLONE_PAGE = 'clone page',
  SAVE_PAGE = 'save page',
  EDIT_PAGE = 'edit page',
  VIEW_CUSTOM_PAGE = 'view custom page',
  DELETE_PAGE = 'delete page',
  AI_ASSISTANT_PROMPT_SUBMIT = 'ai assistant prompt submit',
  GLOBAL_AI_ASSISTANT_OPEN = 'global ai assistant open',
  GLOBAL_AI_ASSISTANT_SUBMIT = 'global ai assistant submit prompt',
  GLOBAL_AI_ASSISTANT_OPTION = 'global ai assistant option selected',
  GLOBAL_AI_ASSISTANT_CHART_APPLIED = 'global ai assistant chart applied',
  GLOBAL_AI_ASSISTANT_PAGE_STREAM_DONE = 'global ai assistant page stream done',
  GLOBAL_AI_ASSISTANT_ROUTE_FILTER_QUEUED = 'global ai assistant route filter queued',
  GLOBAL_AI_ASSISTANT_ROUTE_FILTER_APPLIED = 'global ai assistant route filter applied',
  GLOBAL_AI_ASSISTANT_ROUTE_FILTER_FAILED = 'global ai assistant route filter failed',
  GLOBAL_AI_ASSISTANT_REPORT_OFFERED = 'global ai assistant report offered',
  GLOBAL_AI_ASSISTANT_REPORT_CLICKED = 'global ai assistant report clicked',
  GLOBAL_AI_ASSISTANT_REPORT_PREFILL = 'global ai assistant report prefill',
  GLOBAL_AI_ASSISTANT_REPORT_PREFILL_APPLIED = 'global ai assistant report prefill applied',
}

export type MixpanelEvents =
  | UserTrackingEvents
  | InitiationTrackingEvents
  | CTAInteractionTrackingEvents
  | FeatureTrackingEvents
  | SavedSetsEvents
  | TutorialTrackingEvents;

export type MixpanelUser = {
  id: string;
  client_name: string;
  username?: string;
  trial?: boolean;
  trial_day?: number;
};

export const userTracking = assign(mixpanel, {
  // not typed to avoid circular dependancy by importing from libs/auth
  setUser: (user: MixpanelUser) => {
    // Completely disable tracking for E2E test users
    if (user.username?.includes(E2E_TEST_USER_SHARED_NAME)) {
      mixpanel.opt_out_tracking();
      return;
    }

    // For non-test users, opt in and set user data
    mixpanel.opt_in_tracking();
    mixpanel.identify(user.username);
    mixpanel.people.set(user);
  },
  login: () => mixpanel.track(UserTrackingEvents.LOGIN),
});
